import React from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { DataGrid, DataGridProps } from '@mui/x-data-grid';

export interface DataTableProps extends DataGridProps {
  emptyMessage?: string;
  containerSx?: SxProps<Theme>;
}

/**
 * A standardized DataGrid wrapper component.
 * Enforces consistent styling for column headers, rows, and empty states.
 *
 * Design system specs (aligned with Stitch - Nexus Admin):
 * - Headers: Surface-container-low background (#eff4ff), uppercase 13px text
 * - Rows: White background, hover changes to surface-container-lowest (#ffffff)
 * - Border: 1px outline-variant (#c3c5d9)
 * - Cell padding: 12px vertical (compact density)
 */
export const DataTable: React.FC<DataTableProps> = ({
  rows,
  columns,
  loading,
  emptyMessage = 'No data available',
  containerSx = {},
  sx = {},
  slots = {},
  ...props
}) => {
  return (
    <Box sx={{ height: 600, width: '100%', ...containerSx }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        density="compact"
        disableRowSelectionOnClick
        slots={{
          noRowsOverlay: () => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                gap: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: '#737688', fontFamily: 'Inter, sans-serif' }}
              >
                {emptyMessage}
              </Typography>
            </Box>
          ),
          ...slots,
        }}
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #c3c5d9',
          borderRadius: '12px',
          fontFamily: 'Inter, sans-serif',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#eff4ff',
            borderBottom: '1px solid #c3c5d9',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#eff4ff',
            '&:focus, &:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#737688',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #e5eeff',
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            color: '#0b1c30',
            '&:focus, &:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#f8f9ff',
            },
            '&.Mui-selected': {
              backgroundColor: '#eff4ff',
              '&:hover': {
                backgroundColor: '#f8f9ff',
              },
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #c3c5d9',
            backgroundColor: '#eff4ff',
          },
          '& .MuiTablePagination-root': {
            fontFamily: 'Inter, sans-serif',
            color: '#737688',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: '#ffffff',
          },
          ...sx,
        }}
        {...props}
      />
    </Box>
  );
};
